<template>
    <div>
        <ts-page-title
            :title="$t('uom.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('uom.pageTitle'),
                    active: true
                }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper>
                <div class="tw-flex tw-justify-between tw-items-center">
                    <div>
                        <a-button
                            v-if="$can('create-unit-of-measures')"
                            type="primary"
                            @click="addNew"
                        >
                            {{ $t("addNew") }}
                        </a-button>
                    </div>
                    <div class="tw-space-x-3">
                        <a-tooltip :title="$t('uom.search')">
                            <a-input-search
                                v-model="search"
                                :placeholder="$t('uom.search')"
                                style="width: 200px"
                            />
                        </a-tooltip>
                    </div>
                </div>
            </ts-panel-wrapper>
            <div class="tw-overflow-x-scroll">
                <ts-table
                    :columns="columns"
                    :records="resources"
                    :loading="loading"
                >
                    <template v-slot="{ record }">
                        <td
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.shop_name }}
                        </td>
                        <td
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.uom_name_kh }}
                        </td>
                        <td
                            :class="
                                record.is_deleted
                                    ? 'tw-line-through tw-text-red-600'
                                    : ''
                            "
                        >
                            {{ record.uom_name_en }}
                        </td>
                        <td class="text-left tw-space-x-3">
                            <template v-if="!record.is_deleted">
                                <a
                                    v-if="$can('delete-unit-of-measures')"
                                    href="#"
                                    v-tooltip="$t('edit')"
                                    class="text-primary mr-2"
                                    @click.prevent="edit(record)"
                                >
                                    <i class="fas fa-edit"></i>
                                </a>
                                <a-popconfirm
                                    v-if="$can('delete-unit-of-measures')"
                                    placement="leftTop"
                                    :title="$t('are_you_sure_to_delete')"
                                    :ok-text="$t('yes')"
                                    :cancel-text="$t('no')"
                                    @confirm="deleteRecord(record)"
                                >
                                    <a href="#" :disabled="record._deleting">
                                        <i
                                            class="fas fa-circle-notch spin"
                                            v-if="record._deleting"
                                        ></i>
                                        <i
                                            class="fa fa-trash text-red"
                                            v-else
                                        ></i>
                                    </a>
                                </a-popconfirm>
                            </template>
                        </td>
                    </template>
                </ts-table>
            </div>
            <div class="d-flex justify-content-end tw-p-4">
                <ts-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-pagination>
            </div>
        </ts-panel>

        <!-- form-action -->
        <a-modal
            v-if="showForm"
            v-model="showForm"
            :title="$t('uom.pageTitle')"
            centered
            :maskClosable="false"
            @cancel="clearEdit"
            :zIndex="1020"
						width="400px"
            :footer="null"
        >
            <FormAction @cancel="clearEdit" />
        </a-modal>
    </div>
</template>

<script>
import { debounce } from "lodash";
import FormAction from "./form";
import { mapState } from "vuex";

export default {
    name: "uomIndex",
    components: {
        FormAction
    },
    data() {
        return {
            loading: false,
            showForm: false
        };
    },
    computed: {
        ...mapState("inventory/uom", ["resources", "pagination"]),
        search: {
            get() {
                return this.$store.state.inventory.uom.search;
            },
            set(newValue) {
                this.$store.commit(
                    "inventory/uom/SET_SEARCH",
                    newValue
                );
                this.$store.commit(
                    "inventory/uom/RESET_CURRENT_PAGE"
                );
            }
        },
        columns() {
            return [
                { name: this.$t("uom.shopName"), sortKey: 'shop_name' },
                { name: this.$t("uom.nameKh"), sortKey: 'uom_name_kh' },
                { name: this.$t("uom.nameEn") , sortKey: 'uom_name_en'},
                {
                    name: this.$t("actions"),
                    style: "text-align:center; width: 1px;"
                }
            ];
        }
    },
    methods: {
        fetchData(attributes) {
            this.loading = true;
            this.$store
                .dispatch("inventory/uom/fetch", { ...attributes })
                .then(() => {
                    this.loading = false;
                })
                .catch(error => {
                    this.loading = false;
                    this.$notify({ type: "error", text: error.message });
                });
        },
        addNew() {
            this.showForm = true;
        },
        clearEdit() {
            this.$store.commit("inventory/uom/SET_EDIT_DATA", {});
            this.fetchData();
            this.showForm = false;
        },
        edit(record) {
            this.$store.commit(
                "inventory/uom/SET_EDIT_DATA",
                record
            );
            this.showForm = true;
        },
        deleteRecord(record) {
            record._deleting = true;
            this.$store
                .dispatch(
                    "inventory/uom/destroy",
                    record.uom_id
                )
                .then(response => {
                    this.$notify({ type: "success", text: response.message });
                    this.fetchData();
                })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                })
                .finally(() => {
                    record._deleting = false;
                });
        }
    },
    watch: {
        search: debounce(function() {
            this.fetchData();
        }, 500)
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchData();
        });
    },
    beforeRouteLeave(to, from, next) {
        this.$store.commit("inventory/uom/RESET_STATE");
        next();
    }
};
</script>
